import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import VideoTutorialModal from './VideoTutorialModal';
import './LabNavigation.css';

/**
 * Composant de navigation principal pour l'interface lab
 * Compatible avec l'ancienne logique activeSection ET les nouvelles modales spécialisées
 */
const LabNavigation = ({ 
  onOpenSection,
  activeSection,
  selectedNode,
  nodes = [],
  testScenarios = [],
  relationships = [],
  pendingNodes = []
}) => {
  const { t } = useTranslation();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Données des vidéos tutoriels
  const tutorialVideos = {
    quickCreate: {
      id: 'quickCreate',
      title: t('tutorials.quickCreate.title', 'Text to Node Creator - Création de Nœuds par IA'),
      description: t('tutorials.quickCreate.description', 'Découvrez comment créer des nœuds facilement avec du texte libre ou du Gherkin'),
      duration: '1:18',
      videoId: 'lfR4rK9ggLo', // YouTube: Create a Node
      thumbnail: 'https://img.youtube.com/vi/lfR4rK9ggLo/maxresdefault.jpg'
    },
    nodesList: {
      id: 'nodesList',
      title: t('tutorials.nodesList.title', 'Navigation dans la Liste'),
      description: t('tutorials.nodesList.description', 'Découvrez comment naviguer efficacement dans vos nœuds'),
      duration: '1:53',
      videoId: 'vakIQFHanDE', // Youtube: Nodes List Navigation
      thumbnail: 'https://img.youtube.com/vi/vakIQFHanDE/maxresdefault.jpg'
    },
    relations: {
      id: 'relations',
      title: t('tutorials.relations.title', 'Gestion des Relations'),
      description: t('tutorials.relations.description', 'Créez et visualisez les relations entre vos nœuds'),
      duration: '1:03',
      videoId: 'X1R0E_8r20A', // Youtube: Node Relations 
      thumbnail: 'https://img.youtube.com/vi/X1R0E_8r20A/maxresdefault.jpg'
    },
    tests: {
      id: 'tests',
      title: t('tutorials.tests.title', 'Génération de Tests IA'),
      description: t('tutorials.tests.description', 'Utilisez l\'IA pour générer des tests pertinents'),
      duration: '1:25',
      videoId: 'q1p6c5yjsuE', // YouTube: Create Test
      thumbnail: 'https://img.youtube.com/vi/q1p6c5yjsuE/maxresdefault.jpg'
    },
    importCode: {
      id: 'importCode',
      title: t('tutorials.importCode.title', 'Import de Code'),
      description: t('tutorials.importCode.description', 'Importez automatiquement du code existant'),
      duration: '2:45',
      videoId: 'lfR4rK9ggLo', // Réutilise la vidéo Create a Node pour l'instant
      thumbnail: 'https://img.youtube.com/vi/lfR4rK9ggLo/maxresdefault.jpg'
    },
    analysis: {
      id: 'analysis',
      title: t('tutorials.analysis.title', 'Analyse d\'Impact'),
      description: t('tutorials.analysis.description', 'Analysez l\'impact de vos modifications'),
      duration: '3:30',
      videoId: 'q1p6c5yjsuE', // Réutilise la vidéo Create Test pour l'instant
      thumbnail: 'https://img.youtube.com/vi/q1p6c5yjsuE/maxresdefault.jpg'
    }
    ,
    // Ajout pour que toutes les sections du Laboratory aient un bouton tutoriel
    pendingNodes: {
      id: 'pendingNodes',
      title: t('tutorials.pendingNodes.title', 'Ressources en attente'),
      description: t('tutorials.pendingNodes.description', 'Gérez les éléments en attente et comprenez leur cycle de vie'),
      duration: '1:10',
      videoId: 'lfR4rK9ggLo', // Réutilise la vidéo Create a Node pour l'instant
      thumbnail: 'https://img.youtube.com/vi/lfR4rK9ggLo/maxresdefault.jpg'
    },
    createNode: {
      id: 'createNode',
      title: t('tutorials.createNode.title', 'Créer un nœud'),
      description: t('tutorials.createNode.description', 'Guide rapide pour créer des nœuds et bonnes pratiques'),
      duration: '1:30',
      videoId: 'lfR4rK9ggLo', // Utilise la vidéo Create a Node
      thumbnail: 'https://img.youtube.com/vi/lfR4rK9ggLo/maxresdefault.jpg'
    },
    vectorSearch: {
      id: 'vectorSearch',
      title: t('tutorials.vectorSearch.title', 'Recherche vectorielle'),
      description: t('tutorials.vectorSearch.description', 'Utilisez la recherche vectorielle pour retrouver du contenu similaire'),
      duration: '2:00',
      videoId: 'q1p6c5yjsuE', // Réutilise la vidéo Create Test pour l'instant
      thumbnail: 'https://img.youtube.com/vi/q1p6c5yjsuE/maxresdefault.jpg'
    },
    specifications: {
      id: 'specifications',
      title: t('tutorials.specifications.title', 'Spécifications & Docs'),
      description: t('tutorials.specifications.description', 'Comment créer et maintenir des spécifications utiles'),
      duration: '2:20',
      videoId: 'lfR4rK9ggLo', // Réutilise la vidéo Create a Node pour l'instant
      thumbnail: 'https://img.youtube.com/vi/lfR4rK9ggLo/maxresdefault.jpg'
    },
    jiraImport: {
      id: 'jiraImport',
      title: t('tutorials.jiraImport.title', 'Import JIRA'),
      description: t('tutorials.jiraImport.description', 'Importez automatiquement vos tickets JIRA et générez des nœuds avec l\'IA'),
      duration: '0:44',
      videoId: 'rc8EB-BGQrY', // YouTube: JIRA Integration Overview
      thumbnail: 'https://img.youtube.com/vi/rc8EB-BGQrY/maxresdefault.jpg'
    },
    testCycles: {
      id: 'testCycles',
      title: t('tutorials.testCycles.title', 'Test Cycles - ZephyrMented'),
      description: t('tutorials.testCycles.description', 'Gérez vos cycles de tests avec IA pour priorisation et détection de gaps'),
      duration: '2:15',
      videoId: 'lfR4rK9ggLo', // Réutilise la vidéo Create a Node pour l'instant
      thumbnail: 'https://img.youtube.com/vi/lfR4rK9ggLo/maxresdefault.jpg'
    },
    duplicateDetector: {
      id: 'duplicateDetector',
      title: t('tutorials.duplicateDetector.title', 'Détecteur de Doublons'),
      description: t('tutorials.duplicateDetector.description', 'Détectez automatiquement les nœuds en double grâce à l\'analyse sémantique'),
      duration: '1:45',
      videoId: 'lfR4rK9ggLo',
      thumbnail: 'https://img.youtube.com/vi/lfR4rK9ggLo/maxresdefault.jpg'
    },
    coverageAdvisor: {
      id: 'coverageAdvisor',
      title: t('tutorials.coverageAdvisor.title', 'Conseiller de Couverture'),
      description: t('tutorials.coverageAdvisor.description', 'Analysez la couverture de tests et obtenez des recommandations IA'),
      duration: '2:00',
      videoId: 'lfR4rK9ggLo',
      thumbnail: 'https://img.youtube.com/vi/lfR4rK9ggLo/maxresdefault.jpg'
    },
    relatedFinder: {
      id: 'relatedFinder',
      title: t('tutorials.relatedFinder.title', 'Découvreur de Features Liées'),
      description: t('tutorials.relatedFinder.description', 'Découvrez les features liées à vos nœuds grâce à l\'IA'),
      duration: '1:50',
      videoId: 'lfR4rK9ggLo',
      thumbnail: 'https://img.youtube.com/vi/lfR4rK9ggLo/maxresdefault.jpg'
    }
  };

  const handleVideoClick = (sectionId, event) => {
    event.stopPropagation(); // Empêche l'ouverture de la section
    const video = tutorialVideos[sectionId];
    if (video) {
      setSelectedVideo(video);
      setShowVideoModal(true);
    }
  };

  const navigationSections = [
    {
      id: 'quickCreate',
      icon: '⚡',
      title: t('navigation.quickCreate'),
      description: t('navigation.quickCreateDesc'),
      badge: null,
      primary: true,
      aiBadge: true
    },
    {
      id: 'nodesList',
      icon: '📋',
      title: t('navigation.nodesList'),
      description: t('navigation.nodesListDesc'),
      badge: nodes.length,
      primary: true,
      aiBadge: true
    },
    {
      id: 'relations',
      icon: '🔗',
      title: t('navigation.relations'),
      description: t('navigation.relationsDesc'),
      badge: relationships.length,
      primary: true,
      aiBadge: true
    },
    {
      id: 'tests',
      icon: '🧪',
      title: t('navigation.tests'),
      description: t('navigation.testsDesc'),
      badge: testScenarios.length,
      primary: true,
      aiBadge: true
    },
    {
      id: 'pendingNodes',
      icon: '⏳',
      title: t('modelingElements'),
      description: t('modelingElementsDesc'),
      badge: pendingNodes.length || null,
      primary: true,
      aiBadge: true
    },
    {
      id: 'createNode',
      icon: '�',
      title: t('navigation.createNode'),
      description: t('navigation.createNodeDesc'),
      badge: null,
      primary: false,
      aiBadge: true
    },
    {
      id: 'importCode',
      icon: '📁',
      title: t('navigation.importCode'),
      description: t('navigation.importCodeDesc'),
      badge: null,
      primary: false,
      aiBadge: true
    },
    {
      id: 'vectorSearch',
      icon: '🎯',
      title: t('navigation.vectorSearch'),
      description: t('navigation.vectorSearchDesc'),
      badge: null,
      primary: false,
      aiBadge: true
    },
    {
      id: 'analysis',
      icon: '🔍',
      title: t('navigation.analysis'),
      description: t('navigation.analysisDesc'),
      badge: selectedNode ? '!' : null,
      primary: false,
      aiBadge: true
    },
    {
      id: 'specifications',
      icon: '📖',
      title: t('navigation.specifications'),
      description: t('navigation.specificationsDesc'),
      badge: null,
      primary: false,
      aiBadge: true
    },
    {
      id: 'jiraImport',
      icon: '🔄',
      title: t('navigation.jiraImport', 'Import JIRA'),
      description: t('navigation.jiraImportDesc', 'Importez vos tickets JIRA et générez automatiquement des nœuds avec l\'IA'),
      badge: null,
      primary: false,
      aiBadge: true
    },
    {
      id: 'testCycles',
      icon: '🔄',
      title: t('navigation.testCycles', 'Test Cycles'),
      description: t('navigation.testCyclesDesc', 'Gérez vos cycles de tests avec priorisation IA et détection de gaps'),
      badge: null,
      primary: false,
      aiBadge: true
    },
    {
      id: 'duplicateDetector',
      icon: '🔍',
      title: t('duplicateDetectorTitle'),
      description: t('duplicateDetectorDesc'),
      badge: 'IA',
      primary: false
    },
    {
      id: 'coverageAdvisor',
      icon: '📊',
      title: t('coverageAdvisorTitle'),
      description: t('coverageAdvisorDesc'),
      badge: 'BETA',
      primary: false
    },
    {
      id: 'relatedFinder',
      icon: '🔗',
      title: t('relatedFinderTitle'),
      description: t('relatedFinderDesc'),
      badge: 'IA',
      primary: false
    },
    {
      id: 'export',
      icon: '📤',
      title: t('navigation.export', 'Export'),
      description: t('navigation.exportDesc', 'Exportez vos données en JSON, CSV, Markdown ou HTML'),
      badge: null,
      primary: false,
      aiBadge: false
    },
    {
      id: 'regressionRadar',
      icon: '📡',
      title: t('navigation.regressionRadar', 'Regression Radar'),
      description: t('navigation.regressionRadarDesc', 'Graph-powered regression analysis — Risk Score, Impact Scope & Optimal Test Set'),
      badge: 'NEW',
      primary: false,
      aiBadge: false
    },
    // FARA-7B: masqué temporairement (en attente de provider d'inférence gratuit)
    // {
    //   id: 'exploratoryTests',
    //   icon: '🤖',
    //   title: t('navigation.exploratoryTests', 'Tests Exploratoires FARA-7B'),
    //   description: t('navigation.exploratoryTestsDesc', 'Tests automatiques pilotés par IA — le graphe crée les scénarios, FARA-7B les exécute'),
    //   badge: 'NEW',
    //   primary: false,
    //   aiBadge: true
    // }
  ];

  return (
    <div className="lab-navigation">
      <div className="lab-navigation-header">
        <h3 className="lab-navigation-title">
          {t('navigation.sections')}
        </h3>
        <p className="lab-navigation-subtitle">
          {t('navigation.sectionsDesc')}
        </p>
      </div>

      <div className="lab-navigation-grid">
        <div className="lab-navigation-primary">
          <h4 className="lab-navigation-group-title">
            {t('navigation.primarySections')}
          </h4>
          <div className="lab-navigation-buttons">
            {navigationSections
              .filter(section => section.primary)
              .map(section => (
                <button
                  key={section.id}
                  onClick={() => onOpenSection(section.id)}
                  className={`lab-nav-button primary ${activeSection === section.id ? 'active' : ''}`}
                  title={section.description}
                >
                  <div className="lab-nav-button-content">
                    <div className="lab-nav-button-header">
                      <span className="lab-nav-button-icon">{section.icon}</span>
                      <span className="lab-nav-button-title">{section.title}</span>
                      {section.badge !== null && section.badge !== 'IA' && section.badge !== 'BETA' && (
                        <span className="lab-nav-button-badge">{section.badge}</span>
                      )}
                      {section.aiBadge && (
                        <span className="lab-nav-button-badge ai-badge">IA</span>
                      )}
                      {section.badge === 'BETA' && (
                        <span className="lab-nav-button-badge beta-badge">BETA</span>
                      )}
                      {tutorialVideos[section.id] && (
                        <button
                          onClick={(e) => handleVideoClick(section.id, e)}
                          className="tutorial-video-btn"
                          title={t('tutorials.watchTutorial', 'Regarder le tutoriel')}
                        >
                          🎥
                        </button>
                      )}
                    </div>
                    <p className="lab-nav-button-desc">{section.description}</p>
                  </div>
                </button>
              ))
            }
          </div>
        </div>

        <div className="lab-navigation-secondary">
          <h4 className="lab-navigation-group-title">
            {t('navigation.advancedSections')}
          </h4>
          <div className="lab-navigation-buttons">
            {navigationSections
              .filter(section => !section.primary)
              .map(section => (
                <button
                  key={section.id}
                  onClick={() => onOpenSection(section.id)}
                  className={`lab-nav-button secondary ${activeSection === section.id ? 'active' : ''}`}
                  title={section.description}
                >
                  <div className="lab-nav-button-content">
                    <div className="lab-nav-button-header">
                      <span className="lab-nav-button-icon">{section.icon}</span>
                      <span className="lab-nav-button-title">{section.title}</span>
                      {section.badge !== null && section.badge !== 'IA' && section.badge !== 'BETA' && (
                        <span className="lab-nav-button-badge">{section.badge}</span>
                      )}
                      {section.aiBadge && (
                        <span className="lab-nav-button-badge ai-badge">IA</span>
                      )}
                      {section.badge === 'BETA' && (
                        <span className="lab-nav-button-badge beta-badge">BETA</span>
                      )}
                      {tutorialVideos[section.id] && (
                        <button
                          onClick={(e) => handleVideoClick(section.id, e)}
                          className="tutorial-video-btn"
                          title={t('tutorials.watchTutorial', 'Regarder le tutoriel')}
                        >
                          🎥
                        </button>
                      )}
                    </div>
                    <p className="lab-nav-button-desc">{section.description}</p>
                  </div>
                </button>
              ))
            }
          </div>
        </div>
      </div>
      
      {/* Modal Vidéo Tutoriel */}
      <VideoTutorialModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        video={selectedVideo}
        allVideos={Object.values(tutorialVideos)}
        onVideoSelect={(videoId) => {
          const video = tutorialVideos[videoId];
          if (video) {
            setSelectedVideo(video);
          }
        }}
      />
    </div>
  );
};

export default LabNavigation;